import React, {SyntheticEvent, useEffect, useState} from "react";
import '../../css/components/NewPlan.css';
import {useMultistepForm} from "./components/utils/form/useMultistepForm";
import {GeneralForm} from "./components/newPlanSteps/GeneralForm";
import {Attachment, Exercise, FailedExercise, Plan, PlanExercise, PlanFormData, Stage} from "../../types/domain";
import {ExercisesForm} from "./components/newPlanSteps/ExercisesForm";
import {useLocation, useNavigate} from 'react-router-dom';
import TopicService from "../../services/topic.service";
import StateService from "../../services/state.service";
import CustomModal from "./components/utils/modal/CustomModal";
import PlanService from "../../services/plan.service";
import {Button, LoadingOverlay} from "@mantine/core";
import AppHeader from "../header/AppHeader";
import {Container} from "react-bootstrap";
import {ModalType} from "../../types/UITypes";
import Cookies from "js-cookie";
import UseraccountService from "../../services/useraccount.service";
import * as Sentry from "@sentry/react";
import StageService from "../../services/stage.service";
import {planExercisesNotInFailedExercises} from "./components/utils/helperFunctions";

/**
 * New plan page
 * @author KMU
 */

const INITIAL_DATA: PlanFormData = {
    topic: "",
    stages: [],
    serviceCenter: {
        id: 0,
        name: ""
    },
    trainingGroup: null,
    participant: null,
    date: "",
    prepTimeContent: 1,
    prepTimeOrg: 0.5,
    duration: 2,
    timeSupport: 0,
    newExercises: [],
    newAttachments: [],
    user: {
        id: 0,
        username: "",
        role: null,
        serviceCenters: [],
        active: true,
        firstname: "",
        lastname: "",
        password: ""
    }
}

export function NewPlan() {
    const navigate = useNavigate();

    //state that contains the id of the plan that will be updated if not newly created
    const state = useLocation();

    //form data to be mapped to a plan
    const [data, setData] = useState(INITIAL_DATA)
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: (() => {
        }),
        handleCancel: (() => {
        })
    })

    const [dbPlan, setDbPlan] = useState<any>(state.state || null);
    const [dbStages, setDbStages] = useState<Array<Stage>>([]);
    const [updatePlan, setUpdatePlan] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [failedExercises, setFailedExercises] = useState<FailedExercise[]>([]);

    useEffect(() => {
        StageService.getAll().then((response: any) => {
            let loadedStages = response.data;
            if (loadedStages) {
                const prophStage = loadedStages.pop();
                loadedStages.unshift(prophStage);
            }
            setDbStages(loadedStages);
        }).catch((e: Error) => console.log(e));
    }, []);

    /**
     * Helper function to update the data fields
     * @param fields fields with new values
     */
    function updateFields(fields: Partial<any>) {
        setData(prev => {
            return {...prev, ...fields}
        })
    }

    /**
     * Update model with content
     * @param modal
     */
    function updateModal(modal: ModalType) {
        setModal({
            ...modal,
            show: modal.show,
            title: modal.title,
            body: modal.body,
            decline: modal.decline,
            handleOk: modal.handleOk,
            handleCancel: modal.handleCancel
        })
    }

    //attribute to navigate between the form pages
    const {
        step,
        isFirstStep,
        isLastStep, back, next
    } =
        useMultistepForm([          //set form pages
            <GeneralForm {...data} updateFields={updateFields} updateModal={updateModal} updatePlan={updatePlan}/>,
            <ExercisesForm {...data} updateFields={updateFields} failedExercises={failedExercises}
                           dbStages={dbStages}/>,
        ])

    /* Reset newExercises list for exercises form */
    useEffect(() => {
        if (dbPlan != null) {
            let serviceCenter = null;
            if (dbPlan.group != null) {
                serviceCenter = dbPlan.group.serviceCenter
            } else if (dbPlan.participant != null) {
                serviceCenter = dbPlan.participant.serviceCenter
            }

            let exercises: Array<Exercise> = [];
            dbPlan.exercises.sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!).forEach((planExe: PlanExercise) => {
                exercises.push(planExe.exercise)
            })

            setData({
                ...data, topic: dbPlan.topic.name, serviceCenter: serviceCenter,
                stages: dbPlan.stages, trainingGroup: dbPlan.group, participant: dbPlan.participant,
                date: dbPlan.date, prepTimeOrg: dbPlan.prepTimeOrg,
                prepTimeContent: dbPlan.prepTimeContent, timeSupport: dbPlan.timeSupport,
                duration: dbPlan.duration, newExercises: dbPlan.exercises, newAttachments: [],
                user: dbPlan.user
            })

            setUpdatePlan(true);
        } else {
            //get user from database
            let dbUser;
            UseraccountService.getByName(Cookies.get('username')!).then((response: any) => {
                dbUser = response.data;
                setData({...data, user: dbUser});
            }).catch((e: Error) => {
                console.log(e);
            });

            setData(INITIAL_DATA);
            setUpdatePlan(false);
            updateFields({newExercises: [], newAttachments: [], stages: [], user: dbUser})
        }
    }, [dbPlan]);

    /* Checks access of user */
    useEffect(() => {
        if (!Cookies.get('username')) {
            navigate('/login', {replace: true})
        }
    });

    /**
     * Handler function to navigate back to the plan overview page
     */
    const handleBackToOverview = () => setModal({
        ...modal,
        show: true,
        title: "Hinweis",
        body: "Achtung, jegliche Eingaben werden nicht gespeichert!",
        decline: true,
        handleOk: () => {
            //reset data
            setData(INITIAL_DATA);
            updateFields({newExercises: [], newAttachments: []})
            setModal({...modal, show: false});
            setDbPlan(null);
            navigate('/plans', {replace: true})
        },
        handleCancel: () => setModal({...modal, show: false})
    });

    function showSaveModal() {
        // show success modal and redirect to overview page
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Der Plan wurde erfolgreich zwischengespeichert!",
            decline: true,
            handleOk: () => {
                updateFields({newAttachments: []})
                setModal({...modal, show: false});
            },
            handleCancel: () => {
                setModal({...modal, show: false});
                setDbPlan(null)
                navigate('/plans', {replace: true})
            },
            okText: "Weiter bearbeiten",
            cancelText: "Zurück zur Übersicht"
        })
    }

    function showFinishModal() {
        // show success modal and redirect to overview page
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Die Planung wurde erfolgreich abgeschlossen!",
            decline: false,
            handleOk: () => {
                setModal({...modal, show: false});
                navigate('/plans', {replace: true})
            },
            handleCancel: () => {
            }
        })
    }

    /**
     * Helper function to show modal with error message
     */
    function showErrorModal(message: string) {
        // show error modal and stay on page
        setModal({
            ...modal,
            show: true,
            title: "Fehler",
            body: message,
            decline: true,
            handleOk: () => {
                setModal({...modal, show: false});
            },
            handleCancel: () => {
                setModal({...modal, show: false});
                setDbPlan(null)
                navigate('/plans', {replace: true})
            },
            okText: "Zurück zur Planung",
            cancelText: "Abbrechen und zur Übersicht"
        })
    }

    function showPartialFailureModal() {
        setModal({
            ...modal,
            show: true,
            title: "Fehler beim Speichern",
            body: "Einige Übungen konnten nicht gespeichert werden!",
            decline: true,
            handleOk: () => setModal({...modal, show: false}),
            handleCancel: () => {
                setModal({...modal, show: false});
                navigate('/plans', {replace: true});
            },
            okText: "Zur Planung",
            cancelText: "Abbrechen und zur Übersicht"
        })
    }

    /**
     * Set and reset exercises and attachments after a partial successful
     * create or update call
     * @param result result of the create or update call
     */
    function handlePartialFailure(result: any) {
        if (result.failedExercises?.length > 0) {       // Exercise failure
            setFailedExercises(result.failedExercises);
            const failedExerciseObjects: Array<Exercise> = result.failedExercises
                .map((f: FailedExercise) => f.exercise);

            const savedPlanExercises: Array<PlanExercise> = planExercisesNotInFailedExercises(result.plan?.exercises ?? [], result.failedExercises)
                .sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!) ?? [];

            // wrap failed exercises back into PlanExercises so the list stays uniform
            let pos = savedPlanExercises.length + 1;
            const failedPlanExercises: Array<PlanExercise> = failedExerciseObjects.map(exe => {
                // Find the original PlanExercise to recover the user's selected attachment subset
                const original = data.newExercises.find(pe =>
                    pe.exercise.name === exe.name &&
                    pe.exercise.description === exe.description &&
                    pe.exercise.material === exe.material
                );
                return {
                    id: 0,
                    exercise: exe,
                    attachments: (original?.attachments ?? []).filter(a => a.id && a.id > 0),  // keep only the user's previously-selected saved attachments
                    execution: undefined,
                    interest: undefined,
                    position: pos++
                };
            });

            // only keep attachments for exercises that failed - other ones were saved in database
            const remainingAttachments = data.newAttachments.filter(a =>
                failedExerciseObjects.some((f: Exercise) =>
                    f.name === a.exercise.name &&
                    f.description === a.exercise.description &&
                    f.material === a.exercise.material
                )
            );

            setData({
                ...data,
                newExercises: [...savedPlanExercises, ...failedPlanExercises],
                newAttachments: remainingAttachments
            });

            showPartialFailureModal();
            return;
        } else {        //Plan failure
            showErrorModal("Checken Sie die allgemeinen Planinformationen und versuchen Sie es erneut.");
            return;
        }
    }

    /**
     * Handle fully successful create and update calls
     * @param result result of the create and update calls
     */
    function handleSuccess(result: any) {
        const savedExercises: Array<PlanExercise> = result.plan?.exercises
            ?.sort((a: PlanExercise, b: PlanExercise) => a.position! - b.position!) ?? [];

        setData({
            ...data,
            newExercises: savedExercises
        });
    }

    /**
     * Handle the response of creating/updating a plan
     * @param response the response of the create/update call
     * @param successStatus 200 for create, 201 for update
     * @param plan the plan that was saved
     */
    function handleSavePlanResponse(response: any, successStatus: number, plan: Plan) {
        {
            const result = response.data;
            if (response.status === successStatus) {   //Full success
                setDbPlan(result.plan);
                handleSuccess(result);
            } else if (response.status === 207) {    //Partial success
                if (result.failedExercises?.length > 0) {
                    // exercise failure — plan WAS saved, reload it
                    setDbPlan(result.plan);
                }
                Sentry.captureException(new Error("Partially failed to save exercises"), {
                    extra: {
                        request: {
                            error: "partial",
                            newPlan: JSON.parse(JSON.stringify(plan)),
                            exercises: JSON.parse(JSON.stringify(data.newExercises)),
                            attachments: JSON.parse(JSON.stringify(data.newAttachments)),
                            failedExercises: JSON.parse(JSON.stringify(result.failedExercises))
                        },
                    },
                });
                handlePartialFailure(result);
                return false;
            } else {
                Sentry.captureException(new Error("General plan saving error"), {
                    extra: {
                        request: {
                            error: "general",
                            newPlan: JSON.parse(JSON.stringify(plan)),
                            exercises: JSON.parse(JSON.stringify(data.newExercises)),
                            attachments: JSON.parse(JSON.stringify(data.newAttachments))
                        },
                    },
                });
                showErrorModal("Allgemeiner Speicherfehler. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
                return false;
            }
            return true;
        }
    }

    /**
     * Handle error when creating/updating a plan
     * @param e error that occurred
     * @param plan plan that was created/updated
     */
    function handleSavePlanError(e: Error, plan: Plan) {
        Sentry.captureException(e, {
            extra: {
                request: {
                    newPlan: JSON.parse(JSON.stringify(plan)),
                    exercises: JSON.parse(JSON.stringify(data.newExercises)),
                    attachments: JSON.parse(JSON.stringify(data.newAttachments))
                },
            },
        });
        setIsSaving(false);
        showErrorModal("Allgemeiner Speicherfehler. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.");
        return false;
    }

    /**
     * Save plan in database
     * @param e form event
     */
    async function onSubmit(e: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
        e.preventDefault()
        if (!isLastStep) {
            //get dates without time
            let givenDate = new Date(data.date);
            let today = new Date()

            //date can be max one year in future
            if (givenDate.getFullYear() > today.getFullYear() + 1) {  //plan can be in next year the latest
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Das Datum kann maximal im nächsten Jahr liegen!",
                    decline: false,
                    handleOk: () => setModal({...modal, show: false}),
                    handleCancel: () => {
                    }
                })

                //a stage needs to be selected
            } else if (data.stages.length === 0) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Es wurde dem Plan kein Stadium zugewiesen!",
                    decline: false,
                    handleOk: () => setModal({...modal, show: false}),
                    handleCancel: () => {
                    }
                })

                //either group or single training needs to be selected
            } else if ((data.trainingGroup === null || data.trainingGroup === undefined || data.trainingGroup?.id === 0)
                && (data.participant === null || data.participant === undefined || data.participant?.id === 0)) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Es muss eine Gruppe oder ein/e Teilnehmer*in ausgewählt werden!",
                    decline: false,
                    handleOk: () => setModal({...modal, show: false}),
                    handleCancel: () => {
                    }
                })

                //no special characters in the topic name allowed
            } else if (/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(data.topic)) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Es dürfen keine Sonderzeichen im Thema vorkommen!",
                    decline: false,
                    handleOk: () => setModal({...modal, show: false}),
                    handleCancel: () => {
                    }
                })
            } else {
                //navigate to next form page
                return next()
            }
        } else {
            //at least one exercise needs to be created
            if (data.newExercises.length === 0) {
                setModal({
                    ...modal,
                    show: true,
                    title: "Fehlermeldung",
                    body: "Der Plan muss Übungen enthalten!",
                    decline: false,
                    handleOk: () => setModal({...modal, show: false}),
                    handleCancel: () => {
                    }
                })
            } else {
                setIsSaving(true);
                setFailedExercises([]);

                //always trim whitespaces from topic
                data.topic = data.topic.trim();

                let dbTopic = (await TopicService.getByName(data.topic)).data;
                //topic not in database yet
                if (typeof (dbTopic) === 'string') {
                    dbTopic = {
                        id: 0,
                        name: data.topic
                    }
                }

                //create attachments
                // Construct data to send to API
                const formData = new FormData();

                for (const exeAttachment of data.newAttachments) {
                    const exe = exeAttachment.exercise; //attachment exercise
                    const newExe = data.newExercises.find(e => e.exercise.name === exe.name
                        && e.exercise.description === exe.description && e.exercise.material === exe.material); //exercise in list of new exercises
                    const exerciseIndex = data.newExercises.findIndex(e => e.exercise.name === exe.name
                        && e.exercise.description === exe.description && e.exercise.material === exe.material);

                    let newExeAttachments: Array<Attachment> = [];
                    for (const a of exeAttachment.attachments) {
                        let attachmentTopic = dbTopic;
                        if (!a.isTopicSpecific) {
                            attachmentTopic = (await TopicService.getByName("ALLGEMEIN")).data;
                        }
                        a.topic = attachmentTopic.name;

                        // resolve solution path for this specific Arbeitsblatt
                        let resolvedSolutionPath = "";
                        if (a.type.name === 'Arbeitsblatt' && a.solutionFile) {
                            resolvedSolutionPath = `${attachmentTopic.name}/${a.solutionFile.name}`;
                        }
                        newExeAttachments.push({
                            id: 0,
                            attachmentType: a.type,
                            path: a.name,
                            solutionPath: resolvedSolutionPath,
                            stages: a.stages?.length > 0 ? a.stages : (newExe?.exercise.stages ?? data.stages),  // use exercise stages, fall back to plan stages
                            topic: attachmentTopic
                        });

                        // upload the Arbeitsblatt file
                        const adaptedFile = new File([a.file!], a.file!.name, {type: a.file!.type});
                        formData.append(`files[${exerciseIndex}]`, adaptedFile);

                        // upload the paired Lösungsblatt file if present
                        if (a.type.name === 'Arbeitsblatt' && a.solutionFile) {
                            const adaptedSolution = new File([a.solutionFile], a.solutionFile.name, {type: a.solutionFile.type});
                            formData.append(`files[${exerciseIndex}]`, adaptedSolution);
                        }
                    }

                    // Push new attachments only onto exercise.attachments to avoid duplicate entries
                    if (newExe) {
                        // Clean stale id-0 attachments from both collections before rebuilding
                        exe.attachments = exe.attachments.filter(a => a.id && a.id > 0);
                        newExe.exercise.attachments.push(...newExeAttachments);
                    }
                }

                //get if it is a save or update call
                const submitter = (e.nativeEvent.submitter as HTMLInputElement).name
                let planState;
                if (submitter === "finish") {
                    planState = (await StateService.getByName("im Training")).data;
                } else {
                    planState = (await StateService.getByName("in Planung")).data;
                }

                //set user of exercises, where user is not set yet
                data.newExercises.forEach(planExercise => {
                    if (!planExercise.exercise.user) {
                        planExercise.exercise.user = updatePlan ? dbPlan.user : data.user;
                    }
                });

                let plan: Plan = {
                    id: 0,
                    date: data.date,
                    duration: data.duration,
                    prepTimeContent: data.prepTimeContent,
                    prepTimeOrg: data.prepTimeOrg,
                    timeSupport: data.timeSupport,
                    exercises: data.newExercises,
                    stages: data.stages,
                    state: planState,
                    topic: dbTopic,
                    group: data.trainingGroup,
                    participant: data.participant,
                    notes: [],
                    user: data.user,
                    creationDate: Date.now().toString()
                }

                let hasNoFailureOrError = true;

                if (updatePlan) {
                    plan.id = dbPlan.id;
                    plan.user = dbPlan.user;
                }
                formData.append("plan", new Blob(
                    [JSON.stringify(plan)],
                    {type: "application/json"}
                ));

                try {
                    const response = updatePlan
                        ? await PlanService.updateWholePlanWithAttachments(formData)
                        : await PlanService.createWholePlanWithAttachments(formData);

                    hasNoFailureOrError = handleSavePlanResponse(response, updatePlan ? 200 : 201, plan);
                } catch (e) {
                    hasNoFailureOrError = handleSavePlanError(e as Error, plan);
                }

                if (hasNoFailureOrError) {
                    submitter === "finish" ? showFinishModal() : showSaveModal();
                }
                setIsSaving(false);
            }
        }
    }

    return (
        <div>
            <AppHeader showStartpageIcon={true} showNewPlanIcon={false}
                       showUserIcon={true} showHomeIcon={false} showBigLogo={false}/>
            <Container className="app-content-container">
                <form onSubmit={onSubmit}
                      onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                          if (e.key === "Enter") {
                              e.preventDefault();
                          }
                      }}
                >
                    {step}
                    <div className="buttonRow">
                        <Button onClick={isFirstStep ? handleBackToOverview : back}>
                            {isFirstStep ? "Zurück zur Übersicht" : "Zurück"}
                        </Button>
                        <Button type="submit" name="save"
                                disabled={isSaving}>{isLastStep ? "Zwischenspeichern" : "Weiter"}</Button>
                        {
                            isLastStep ? <Button type="submit" name="finish" disabled={isSaving}>Planung
                                abschließen</Button> : <></>
                        }
                    </div>
                </form>
                <CustomModal title={modal.title}
                             body={modal.body}
                             decline={modal.decline} showModal={modal.show} handleOk={modal.handleOk}
                             handleCancel={modal.handleCancel} okText={modal.okText} cancelText={modal.cancelText}
                />
            </Container>
            <LoadingOverlay
                className="fullscreen-overlay"
                visible={isSaving}
                loaderProps={{size: 'lg', color: 'blueish'}}
                overlayOpacity={0.5}
                overlayColor="#c5c5c5"
                overlayBlur={3}
            />
        </div>
    )
}